import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PackingListService } from '../../services/packingList.service';
import { PERMISSIONS } from '../../constants/permissions';
import { PackingListDTO } from '../../interfaces/commercial.interface';
import { formatDate } from '../../utils/dateUtils';
import { sortByDateDescFutureLast } from '../../utils/listDefaults';

/** Packing list register (15.2) — mirrors ExportLcList's list/paginate shape. Reuses EXP_VIEW/
 * EXP_MANAGE (spec §3 — no new permission catalog entry for this lane). */
const PackingListList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<PackingListDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const res = await PackingListService.list(institutionId, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load packing lists');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.plList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Packing Lists</Typography>
                {canManage && (
                    <Button data-help="commercial.plList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/packing-lists/new')}>
                        New Packing List
                    </Button>
                )}
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.plList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.plList.col.number">Packing List #</TableCell>
                                <TableCell data-help="commercial.plList.col.date">Date</TableCell>
                                <TableCell data-help="commercial.plList.col.destination">Destination</TableCell>
                                <TableCell align="right" data-help="commercial.plList.col.cartons">Cartons</TableCell>
                                <TableCell align="right" data-help="commercial.plList.col.qty">Quantity</TableCell>
                                <TableCell align="right" data-help="commercial.plList.col.net">Net Weight</TableCell>
                                <TableCell align="right" data-help="commercial.plList.col.gross">Gross Weight</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortByDateDescFutureLast(rows, x => x.packingDate).map(row => (
                                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/packing-lists/${row.id}`)}>
                                    <TableCell>{row.packingListNumber}</TableCell>
                                    <TableCell>{formatDate(row.packingDate)}</TableCell>
                                    <TableCell>{row.destination ?? '—'}</TableCell>
                                    <TableCell align="right">{row.totalCartons}</TableCell>
                                    <TableCell align="right">{row.totalQuantity}</TableCell>
                                    <TableCell align="right">{row.totalNetWeight}</TableCell>
                                    <TableCell align="right">{row.totalGrossWeight}</TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center">No packing lists found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default PackingListList;
